// src/pages/integrations/CredentialAttachModal.jsx — SOUND-709
//
// "Use existing credential" — THE ANSWER TO SOUND-615.
//
// Kristina asked whether an NMI credential on a Kurv MID could be COPIED to a
// PayArc MID. The honest answer is that it should never need copying: NMI is
// gateway-level and one gateway already serves MIDs across several processors.
// Gateway 1310948 carries 6 MIDs across Nexio, Nuvei, Kurv, PayArc and Netevia
// on ONE credential — the exact case in the ticket.
//
// So this attaches, and it CANNOT copy. It posts a target to
// /credentials/:id/attach, which writes an edge and never reads a secret value.
// A copy feature would resolve the symptom by duplicating a secret: two records,
// two SSM parameters, two things to rotate, and two to revoke when a gateway
// closes. That is the failure the whole epic exists to prevent.
import React, { useState, useEffect, useMemo } from "react";
import { makeApiRequest, apiRoutes } from "constants/apiRoutes";
import { useAuth } from "utilities/auth";
import { useToast } from "components/Toast";
import { X, Link2 } from "lucide-react";

export default function CredentialAttachModal({ credential, onClose, onAttached }) {
  const { user } = useAuth();
  const { toast } = useToast();
  const auth = useMemo(() => ({ id: user?.id, accessToken: user?.accessToken }), [user?.id, user?.accessToken]);
  // See CredentialCreateModal: the header is who is asking, not who is asked about.
  const callerCompanyId = user?.companyId;

  const [companies, setCompanies] = useState([]);
  // Defaults to the credential's OWNER, but is changeable — the whole point is
  // that a sound-admin credential is consumed by other companies' MIDs. 110 of
  // 110 pointers in prod cross a company boundary.
  const [companyId, setCompanyId] = useState(credential?.companyId || "");
  const [dbas, setDbas] = useState([]);
  const [mids, setMids] = useState([]);
  const [dbaId, setDbaId] = useState("");
  const [midId, setMidId] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    makeApiRequest("get", `${apiRoutes.getCompanies}?limit=500`, auth, callerCompanyId)
      .then((res) => setCompanies(res.data?.data ?? []))
      .catch(() => setCompanies([]));
  }, [auth, callerCompanyId]);

  useEffect(() => {
    if (!companyId) { setDbas([]); setMids([]); return; }
    makeApiRequest("get", apiRoutes.credentialLinkTargets(companyId), auth, callerCompanyId)
      .then((res) => { setDbas(res.data?.data?.dbas ?? []); setMids(res.data?.data?.mids ?? []); })
      .catch(() => { setDbas([]); setMids([]); });
  }, [auth, companyId, callerCompanyId]);

  const alreadyLinked = new Set(
    (credential?._consumers?.midLinks || []).map((l) => l.otherId)
  );

  async function submit(e) {
    e.preventDefault();
    if (!midId) return;
    setSaving(true);
    try {
      await makeApiRequest("post", apiRoutes.attachCredential(credential.credentialId), auth, callerCompanyId, {
        targetType: "mid",
        targetId: midId,
      });
      toast({ title: "Credential attached", description: "A link was created. The secret was not copied." });
      onAttached?.();
      onClose();
    } catch (err) {
      toast({ status: "destructive", title: "Could not attach", description: err.response?.data?.error || err.message });
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={onClose}>
      <div className="surface w-full max-w-lg rounded-xl p-6 shadow-lg ring-1 ring-border" onClick={(e) => e.stopPropagation()}>
        <div className="mb-4 flex items-center justify-between">
          <h3 className="text-lg font-semibold">Use this credential on another MID</h3>
          <button className="btn btn-ghost h-8 w-8 p-0" onClick={onClose} aria-label="Close"><X className="h-4 w-4" /></button>
        </div>

        <p className="mb-4 rounded border border-border bg-muted/40 px-3 py-2 text-xs text-muted-foreground">
          This creates a <strong>link</strong>. The secret is not copied — one credential can serve many
          MIDs, including across different processors.
        </p>

        <form onSubmit={submit} className="space-y-4">
          <label className="block">
            <span className="mb-1 block text-xs text-muted-foreground">Company</span>
            <select className="select w-full" value={companyId} onChange={(e) => { setCompanyId(e.target.value); setDbaId(""); setMidId(""); }}>
              <option value="">Select a company…</option>
              {companies.map((c) => <option key={c.companyId} value={c.companyId}>{c.name}</option>)}
            </select>
          </label>

          {companyId && (
            <>
              <label className="block">
                <span className="mb-1 block text-xs text-muted-foreground">DBA <span className="opacity-60">— narrows the MID list</span></span>
                <select className="select w-full" value={dbaId} onChange={(e) => { setDbaId(e.target.value); setMidId(""); }}>
                  <option value="">All DBAs</option>
                  {dbas.map((d) => <option key={d.dbaId} value={d.dbaId}>{d.dbaName}</option>)}
                </select>
              </label>

              <label className="block">
                <span className="mb-1 block text-xs text-muted-foreground">MID</span>
                <select className="select w-full" value={midId} onChange={(e) => setMidId(e.target.value)} required>
                  <option value="">Select a MID…</option>
                  {mids
                    .filter((m) => !dbaId || m.dbaId === dbaId)
                    .map((m) => (
                      <option key={m.midId} value={m.midId} disabled={alreadyLinked.has(m.midId)}>
                        {[m.descriptor, m.processorName, m.mid].filter(Boolean).join(" · ")}
                        {alreadyLinked.has(m.midId) ? "  (already linked)" : ""}
                      </option>
                    ))}
                </select>
                {!mids.length && <p className="mt-1 text-xs text-muted-foreground">No MIDs for this company.</p>}
              </label>
            </>
          )}

          <div className="flex justify-end gap-2 pt-2">
            <button type="button" className="btn btn-ghost" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={saving || !midId}>
              <Link2 className="mr-1 h-4 w-4" /> {saving ? "Attaching…" : "Attach"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
